import { Timestamp } from "../compat";

/**
 * SET.Live light show (ENG-2291): during a show, fans' phones display a
 * full-screen color, pattern or custom look so the crowd becomes part of the
 * light show. Looks are set up in the editor, published with the event, and
 * triggered live from Backstage through {@link LightShowLive}.
 */

export const LightShowSpeeds = ["slow", "medium", "fast"] as const;
export type LightShowSpeed = (typeof LightShowSpeeds)[number];

export const SolidMotions = ["none", "fade", "pulse", "twinkle"] as const;
export type SolidMotion = (typeof SolidMotions)[number];

export const PatternMotions = [
  "none",
  "scrollUp",
  "scrollDown",
  "radiate",
] as const;
export type PatternMotion = (typeof PatternMotions)[number];

export const PatternIds = [
  "keyboard",
  "wavelength",
  "radial",
  "stars",
  "stripes",
  "chevrons",
  "digital",
  "hearts",
  "triangles",
  "lightning",
  "emoji",
  "confetti",
] as const;
export type PatternId = (typeof PatternIds)[number];

/**
 * Custom looks are designed pieces with nothing to set up: each draws itself
 * (see ./custom), including its own colors and motion.
 */
export const CustomLookIds = [
  "homeFree",
  "homeFreeMulticolor",
  "homeFreeTwinkle",
  "homeFreeCassette",
  "syncTest",
] as const;
export type CustomLookId = (typeof CustomLookIds)[number];

export const CUSTOM_LOOK_NAMES: Record<CustomLookId, string> = {
  homeFree: "Home Free",
  homeFreeMulticolor: "Home Free Multicolor",
  homeFreeTwinkle: "Home Free Twinkle",
  homeFreeCassette: "Home Free Cassette",
  syncTest: "Sync Test",
};

interface LightShowLookBase {
  id: string;
  /** Admin-facing label ("Name this Look"). */
  name: string;
  /** Join prompt title ("Launching Title"); falls back to the default copy. */
  title?: string;
  /** Join prompt subtitle ("Launching Subtitle"); falls back to the default copy. */
  subtitle?: string;
}

export interface SolidLook extends LightShowLookBase {
  type: "solid";
  color: string;
  motion: SolidMotion;
}

export interface ColorShiftLook extends LightShowLookBase {
  type: "colorShift";
  /** Blended in order and looped; at least two. */
  colors: string[];
  speed: LightShowSpeed;
}

export interface PatternLook extends LightShowLookBase {
  type: "pattern";
  pattern: PatternId;
  /** [Color 1 (shapes), Color 2 (background)]. */
  colors: [string, string];
  /** Only for the `emoji` pattern. */
  emoji?: string;
  motion: PatternMotion;
  speed: LightShowSpeed;
}

export interface CustomLook extends LightShowLookBase {
  type: "custom";
  customLookId: CustomLookId;
}

/**
 * "sync": every phone shows the same frame. "random": each phone plays from
 * its own point in the loop, so across the crowd it looks random.
 */
export const AnimationTimings = ["sync", "random"] as const;
export type AnimationTiming = (typeof AnimationTimings)[number];

/**
 * A Lottie animation uploaded in the editor (ENG-2347), so a new designed look
 * is just a file: no code and no deploy (see ./animation). It fills the screen
 * on black, cropping its edges on phones shaped differently from it, so files
 * are made 1080×2340 with their own background and the important parts within
 * {@link ANIMATION_SAFE_AREA}.
 */
export interface AnimationLook extends LightShowLookBase {
  type: "animation";
  /** The Lottie JSON. Named by its content, so what's at the URL never changes. */
  src: string;
  /** The uploaded file's name, for the editor. */
  fileName?: string;
  timing: AnimationTiming;
  /** Its main colors, read from the file, for swatches. */
  colors: string[];
  /**
   * Seconds into the loop to draw a still (a thumbnail), from a "still" marker
   * in the file; the start when missing.
   */
  stillTime?: number;
}

export type LightShowLook =
  SolidLook | ColorShiftLook | PatternLook | CustomLook | AnimationLook;

/**
 * The part of a 1080×2340 animation that shows on phones from 9:16 to 9:21;
 * outside it may be cropped.
 */
export const ANIMATION_SAFE_AREA = {
  left: 40,
  top: 210,
  width: 1000,
  height: 1920,
};

export type LightShowGate = "none" | "checkin";

/** Published with the event; show runners can go live with any of these looks. */
export interface LightShowConfig {
  enabled?: boolean;
  /** "checkin": fans must check in before they can join. */
  gate?: LightShowGate;
  looks: LightShowLook[];
}

/**
 * How far ahead of a show runner's tap a change takes effect, so every phone
 * has the update before it starts and switches on the same frame.
 */
export const LIGHT_SHOW_START_DELAY_MS = 1000;

/**
 * The live state at `set_fresh_events/{eventId}/live/lightShow`, written by
 * Backstage. Kept out of the event docs so switching looks doesn't fire their
 * write triggers.
 */
export interface LightShowLive<T = Timestamp> {
  /**
   * A copy of the look that went live, so republishing the event mid-show
   * doesn't change what fans see. `null` when no look is live.
   */
  look: LightShowLook | null;
  /**
   * Server time of the show runner's change. The change takes effect at
   * `startedAt + startDelayMs`, and fans time the look's animation from there.
   */
  startedAt: T | null;
  /** See {@link LIGHT_SHOW_START_DELAY_MS}; 0 when missing. */
  startDelayMs?: number;
  updatedAt: T;
  /** Uid of the show runner who made the change. */
  updatedBy?: string;
}
